import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import '../../Css/tables/tabla_consultar.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { API_URLS } from '../../Services/servicioURL';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';

import { registrarEntidad, obtenerEntidades, actualizarProveedor, eliminarEntidad } from '../../Services/serviciohttp';

interface RegistroItem {
    id: string;
    nombre: string;
    cve?: string;
}

const Proveedores: React.FC = () => {
    const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
    const [id, setId] = useState('');
    const [nombre, setNombre] = useState('');
    const [mostrarTabla, setMostrarTabla] = useState(false);
    const [data, setData] = useState<RegistroItem[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const toast = useRef<Toast>(null);

    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        const searchTermLower = searchTerm.toLowerCase();
        return data.filter(item =>
            String(item.id).toLowerCase().includes(searchTermLower) ||
            String(item.nombre).toLowerCase().includes(searchTermLower)
        );
    }, [data, searchTerm]);

    useEffect(() => {
        if (mostrarTabla) {
            obtenerRegistros();
        }
    }, [mostrarTabla]);

    const obtenerRegistros = async () => {
        try {
            const result = await obtenerEntidades(API_URLS.proveedores.obtener);
            setData(result);
        } catch (error) {
            console.error('Error al obtener los proveedores:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar los datos de proveedores',
                life: 3000
            });
        }
    };

    const handleNuevo = () => {
        setId('');
        setNombre('');
        setMode('new');
    };

    const handleEditar = () => {
        if (!id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione un proveedor para editar',
                life: 3000
            });
            return;
        }
        setMode('edit');
    };

    const handleGuardar = async () => {
        try {
            if (!nombre) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Campos requeridos',
                    detail: 'Debe completar todos los campos obligatorios',
                    life: 3000
                });
                return;
            }

            if (mode === 'new') {
                await registrarEntidad(API_URLS.proveedores.registrar, '', nombre);
                toast.current?.show({
                    severity: 'success',
                    summary: '¡Éxito!',
                    detail: 'Proveedor creado exitosamente',
                    life: 3000
                });
            } else if (mode === 'edit') {
                await actualizarProveedor(API_URLS.proveedores.actualizar(id), id, nombre);
                toast.current?.show({
                    severity: 'success',
                    summary: '¡Actualizado!',
                    detail: 'Proveedor modificado exitosamente',
                    life: 3000
                });
            }

            setId('');
            setNombre('');
            setMode('view');
            obtenerRegistros();
        } catch (error) {
            console.error('Error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo guardar el proveedor',
                life: 3000
            });
        }
    };

    const handleEliminar = async () => {
        if (!id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione un proveedor para eliminar',
                life: 3000
            });
            return;
        }

        try {
            const confirmacion = window.confirm('⚠️ ¿Está seguro de eliminar este proveedor?\n\nEsta acción no se puede deshacer.');
            if (!confirmacion) return;

            await eliminarEntidad(API_URLS.proveedores.eliminar(id));

            toast.current?.show({
                severity: 'success',
                summary: '¡Eliminado!',
                detail: 'Proveedor eliminado exitosamente',
                life: 3000
            });

            setId('');
            setNombre('');
            setMode('view');
            obtenerRegistros();
        } catch (error) {
            console.error('Error al eliminar:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo eliminar el proveedor',
                life: 3000
            });
        }
    };

    const handleCancelar = () => {
        setId('');
        setNombre('');
        setMode('view');
    };

    const handleConsultar = () => {
        setMostrarTabla(true);
    };

    const cerrarTabla = () => {
        setMostrarTabla(false);
        setSearchTerm('');
    };

    const handleSeleccionarRegistro = (registro: RegistroItem) => {
        setId(String(registro.id));
        setNombre(registro.nombre);
        setMostrarTabla(false);
        setMode('view');
        setSearchTerm('');
    };

    const getModeIndicator = () => {
        switch (mode) {
            case 'new':
                return { text: 'NUEVO PROVEEDOR', color: '#10b981', icon: 'pi-plus' };
            case 'edit':
                return { text: 'EDITANDO PROVEEDOR', color: '#f59e0b', icon: 'pi-pencil' };
            default:
                return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            {/* Modal de consulta mejorado con scroll */}
            {mostrarTabla && (
                <div className="overlay" onClick={cerrarTabla} style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '700px',
                        width: '90%',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        {/* Header fijo */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                🏢 Consultar Proveedores
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={cerrarTabla}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        {/* Barra de búsqueda fija */}
                        <div style={{ marginBottom: '20px', flexShrink: 0 }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        paddingLeft: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '0.95rem'
                                    }}
                                    placeholder="Buscar por ID o nombre..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Contador de resultados fijo */}
                        <div style={{ 
                            marginBottom: '15px', 
                            fontSize: '0.9em', 
                            color: '#6b7280',
                            padding: '8px 12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            flexShrink: 0
                        }}>
                            📊 Mostrando <strong>{filteredData.length}</strong> de <strong>{data.length}</strong> proveedores
                        </div>

                        {/* Contenedor de tabla con scroll */}
                        <div style={{ 
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px', 
                            overflow: 'hidden',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Header de tabla fijo */}
                            <div style={{ 
                                backgroundColor: '#f8fafc',
                                borderBottom: '2px solid #e5e7eb',
                                flexShrink: 0
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'left', 
                                                fontWeight: '600', 
                                                color: '#374151',
                                                width: '25%'
                                            }}>🆔 ID</th>
                                            <th style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'left', 
                                                fontWeight: '600', 
                                                color: '#374151',
                                                width: '75%'
                                            }}>🏢 Nombre</th>
                                        </tr>
                                    </thead>
                                </table>
                            </div>
                            
                            {/* Cuerpo de tabla con scroll */}
                            <div style={{ 
                                flex: 1,
                                overflowY: 'auto',
                                maxHeight: 'calc(80vh - 200px)'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        {filteredData.length > 0 ? (
                                            filteredData.map((item, index) => (
                                                <tr 
                                                    key={index} 
                                                    onClick={() => handleSeleccionarRegistro(item)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                >
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        borderBottom: '1px solid #f3f4f6',
                                                        width: '25%',
                                                        fontWeight: '600',
                                                        color: '#374151'
                                                    }}>{item.id}</td>
                                                    <td style={{ 
                                                        padding: '12px 16px', 
                                                        borderBottom: '1px solid #f3f4f6',
                                                        width: '75%'
                                                    }}>{item.nombre}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={2} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                                    📭 No se encontraron proveedores
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                
                {/* Header principal modernizado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{ 
                            margin: 0, 
                            color: '#1f2937', 
                            fontSize: '2rem', 
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            🏢 Gestión de Proveedores
                        </h1>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: modeInfo.color,
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                        }}>
                            <i className={`pi ${modeInfo.icon}`}></i>
                            {modeInfo.text}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Formulario principal modernizado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    maxWidth: '800px',
                    margin: '0 auto',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {/* Formulario */}
                    <div style={{ marginBottom: '24px' }}>
                        <form autoComplete="off">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '20px', alignItems: 'end' }}>
                                {/* Campo ID */}
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        🆔 ID
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            backgroundColor: '#f9fafb'
                                        }}
                                        value={id}
                                        onChange={(e) => setId(e.target.value)}
                                        disabled
                                    />
                                </div>

                                {/* Campo Nombre */}
                                <div>
                                    <label style={{ 
                                        display: 'block', 
                                        fontSize: '0.95rem', 
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        🏢 Nombre <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            fontSize: '0.95rem',
                                            padding: '0 12px',
                                            border: `2px solid ${mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px',
                                            backgroundColor: mode === 'view' ? '#f9fafb' : '#ffffff'
                                        }}
                                        value={nombre}
                                        onChange={(e) => setNombre(e.target.value)}
                                        disabled={mode === 'view'}
                                        placeholder="Ingrese el nombre del proveedor"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Botones en una sola línea */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {mode === 'view' ? (
                            <>
                                <Button
                                    icon="pi pi-search"
                                    label="Consultar"
                                    className="p-button-info p-button-raised"
                                    onClick={handleConsultar}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px'
                                    }}
                                />
                                <Button
                                    icon="pi pi-plus"
                                    label="Nuevo"
                                    className="p-button-success p-button-raised"
                                    onClick={handleNuevo}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px'
                                    }}
                                />
                                <Button
                                    icon="pi pi-pencil"
                                    label="Editar"
                                    className="p-button-warning p-button-raised"
                                    onClick={handleEditar}
                                    disabled={!id}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px',
                                        opacity: !id ? 0.5 : 1
                                    }}
                                />
                                <Button
                                    icon="pi pi-trash"
                                    label="Eliminar"
                                    className="p-button-danger p-button-raised"
                                    onClick={handleEliminar}
                                    disabled={!id}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px',
                                        opacity: !id ? 0.5 : 1
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <Button
                                    icon="pi pi-check"
                                    label="Guardar"
                                    className="p-button-success p-button-raised"
                                    onClick={handleGuardar}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px'
                                    }}
                                />
                                <Button
                                    icon="pi pi-times"
                                    label="Cancelar"
                                    className="p-button-secondary p-button-raised"
                                    onClick={handleCancelar}
                                    style={{ 
                                        height: '44px',
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        minWidth: '120px'
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Proveedores;